// src/utils/authErrors.js
// Turns sign-in service (Firebase) errors into plain messages that tell
// people what to do, plus which field to fix. `tone: 'info'` marks
// situations that aren't really mistakes (e.g. closing the Google window).
//
// context: 'signin' (default) | 'reauth' (confirming it's you)

const MESSAGES = {
  // Google pop-up
  'auth/popup-closed-by-user'   : { tone: 'info', message: 'The Google sign-in window was closed before you finished. Click "Continue with Google" to try again.' },
  'auth/cancelled-popup-request': { tone: 'info', message: 'The Google sign-in window was closed before you finished. Click "Continue with Google" to try again.' },
  'auth/user-cancelled'         : { tone: 'info', message: 'Sign-in was cancelled. You can try again whenever you are ready.' },
  'auth/popup-blocked'          : { message: 'Your browser blocked the Google sign-in window. Allow pop-ups for fax.crazypixels.org, then click "Continue with Google" again.' },
  'auth/account-exists-with-different-credential': { message: 'This email already signs in a different way. Use the method you used before (email and password, or Google).' },
  'auth/unauthorized-domain'    : { message: 'Sign-in is not allowed from this web address. Please open the portal at fax.crazypixels.org/portal.' },

  // Email and password
  'auth/invalid-email'          : { field: 'email', message: 'That email address doesn\'t look right. Please check it and try again.' },
  'auth/missing-email'          : { field: 'email', message: 'Please enter your email address.' },
  'auth/missing-password'       : { field: 'password', message: 'Please enter your password.' },
  'auth/invalid-credential'     : { field: 'password', message: 'The email or password is incorrect. Check both and try again, or use "Forgot password?".' },
  'auth/invalid-login-credentials': { field: 'password', message: 'The email or password is incorrect. Check both and try again, or use "Forgot password?".' },
  'auth/wrong-password'         : { field: 'password', message: 'The email or password is incorrect. Check both and try again, or use "Forgot password?".' },
  'auth/user-not-found'         : { field: 'email', message: 'The email or password is incorrect. Check both and try again, or use "Forgot password?".' },
  'auth/email-already-in-use'   : { field: 'email', message: 'An account with this email already exists. Sign in instead, or use "Forgot password?".' },
  'auth/weak-password'          : { field: 'password', message: 'Please choose a stronger password with at least 8 characters.' },
  'auth/user-disabled'          : { message: 'This account has been switched off. Please contact your administrator.' },

  // Two-factor codes
  'auth/invalid-verification-code': { field: 'code', message: 'That code didn\'t work. Enter the current 6-digit code from your authenticator app. Codes change every 30 seconds.' },
  'auth/missing-code'           : { field: 'code', message: 'Please enter the 6-digit code from your authenticator app.' },
  'auth/code-expired'           : { field: 'code', message: 'That code has expired. Enter the current 6-digit code from your authenticator app.' },
  'auth/multi-factor-info-not-found': { message: 'Two-factor sign-in isn\'t set up correctly for this account. Please contact your administrator.' },

  // Service / connection
  'auth/too-many-requests'      : { message: 'Too many attempts. Please wait a few minutes before trying again, or use "Forgot password?".' },
  'auth/network-request-failed' : { message: 'Can\'t reach the sign-in service. Check your internet connection and try again.' },
  'auth/internal-error'         : { message: 'Something went wrong while signing in. Please try again in a moment.' },
  'auth/requires-recent-login'  : { message: 'For your security, please confirm it\'s you and try again.' },
  'auth/operation-not-allowed'  : { message: 'This sign-in option isn\'t switched on for the portal. Please contact your administrator.' },
  'auth/admin-restricted-operation': { message: 'This action is restricted. Please contact your administrator.' },
};

const REAUTH = {
  'auth/invalid-credential'     : { field: 'password', message: 'Your current password is incorrect.' },
  'auth/invalid-login-credentials': { field: 'password', message: 'Your current password is incorrect.' },
  'auth/wrong-password'         : { field: 'password', message: 'Your current password is incorrect.' },
};

export function describeAuthError(error, context = 'signin') {
  const code = error?.code || '';
  const hit  = (context === 'reauth' && REAUTH[code]) || MESSAGES[code];
  if (hit) return { tone: 'error', field: null, ...hit, code };
  // Unknown: a readable message, with the code in brackets for support
  const raw = String(error?.message || '').replace(/^Firebase:\s*/i, '').replace(/\s*\(auth\/[^)]*\)\.?$/, '');
  return {
    tone   : 'error',
    field  : null,
    code,
    message: `${raw && raw !== 'Error' ? raw + ' ' : 'Something went wrong. '}Please try again${code ? ` (${code.replace('auth/', '')})` : ''}.`,
  };
}
